import { useEffect, useMemo, useRef, useState } from 'react';
import { BsPlayFill } from '@react-icons/all-files/bs/BsPlayFill';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

import { useViewport } from 'context/viewport';
import styles from 'styles/VideoPlayer.module.scss';
import { StyledComponent } from 'types';

type PlayIconProps = {
  size: number;
  opacity?: number;
  playIcon?: StyledComponent;
  blur?: number;
  playing?: boolean;
  opacityOnHover?: number;
} & StyledComponent;

export const PlayIcon: React.FC<PlayIconProps> = ({
  size,
  opacity = 0.95,
  blur = 3,
  className,
  style,
  playIcon,
  opacityOnHover = 0.7,
  playing
}) => {
  const playIconBgRef = useRef<HTMLDivElement>(null);
  const [isPlayIconHovered, setIsPlayIconHovered] = useState<boolean>(false);
  const { isMobile } = useViewport();

  useEffect(() => {
    const checkMouseMove = throttle(({ target }: Event) => {
      const isMouseWithinPlayIcon = target && playIconBgRef.current && playIconBgRef.current.contains(target as Node);

      setIsPlayIconHovered(!!isMouseWithinPlayIcon);

      return;
    }, 100);

    document.addEventListener('mousemove', checkMouseMove);

    return () => document.removeEventListener('mousemove', checkMouseMove);
  }, []);

  const changeOpacityOnHover = useMemo(
    () => isPlayIconHovered && playing !== undefined && !playing && !isMobile,
    [isPlayIconHovered, playing, isMobile]
  );

  return (
    <div
      className={classNames('position-absolute top-50 start-50 translate-middle', styles['play-icon'], className)}
      style={style}
    >
      <div
        className={classNames(
          'd-flex justify-content-center align-items-center',
          styles['play-icon-bg'],
          playIcon?.className
        )}
        style={{
          opacity: changeOpacityOnHover ? opacityOnHover : opacity,
          width: size,
          height: size,
          backdropFilter: `blur(${blur}px)`,
          WebkitBackdropFilter: `blur(${blur}px)`,
          ...playIcon?.style
        }}
        ref={playIconBgRef}
      >
        <BsPlayFill className='pe-none' fill='white' size={size * 0.5} />
      </div>
    </div>
  );
};
